/**
 * Sezioni del verbale (piano 1 dei verbali, task 2): funzioni pure.
 *
 * Un verbale è UNA stringa HTML; le sue sezioni sono i blocchi che iniziano con
 * un `<h2>`. Il testo prima del primo `<h2>` è la sezione `preambolo` (senza
 * titolo). Solo stringhe, nessun DOMParser: il file è speculare a
 * `backend/utils/verbaleSezioni.js` (stessi nomi, stesse uscite), più
 * `controlloFinale` per la pubblicazione.
 */

export type Sezione = { chiave: string; titolo: string; html: string };

export type EsitoControllo = { livello: 'blocco' | 'avviso'; testo: string };

type Parte = { chiave: string; titolo: string; inizioCorpo: number; fineCorpo: number };

const RE_H2 = /<h2(?:\s[^>]*)?>([\s\S]*?)<\/h2\s*>/gi;

const CHIAVE_PREAMBOLO = 'preambolo';

const ENTITA: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0',
  agrave: 'à', aacute: 'á', egrave: 'è', eacute: 'é', igrave: 'ì', iacute: 'í',
  ograve: 'ò', oacute: 'ó', ugrave: 'ù', uacute: 'ú',
  Agrave: 'À', Egrave: 'È', Eacute: 'É', Igrave: 'Ì', Ograve: 'Ò', Ugrave: 'Ù',
  laquo: '«', raquo: '»', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  hellip: '…', ndash: '–', mdash: '—', euro: '€', deg: '°',
};

/** Decodifica le entità HTML (in un solo passaggio: `&amp;lt;` ⇒ `&lt;`). */
function decodificaEntita(testo: string): string {
  return testo.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (intera: string, nome: string) => {
    if (nome.charAt(0) === '#') {
      const esadecimale = nome.charAt(1) === 'x' || nome.charAt(1) === 'X';
      const codice = parseInt(nome.slice(esadecimale ? 2 : 1), esadecimale ? 16 : 10);
      if (!Number.isFinite(codice) || codice <= 0 || codice > 0x10ffff) return intera;
      return String.fromCodePoint(codice);
    }
    return Object.prototype.hasOwnProperty.call(ENTITA, nome) ? ENTITA[nome] : intera;
  });
}

/** Testo semplice di un frammento HTML (senza tag né commenti, entità decodificate). */
function testoDaHtml(html: string): string {
  return decodificaEntita(
    String(html || '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, '')
  );
}

/** Titolo di un `<h2>`: testo senza tag, spazi compattati. */
function testoTitolo(interno: string): string {
  return testoDaHtml(interno).replace(/[\s\u00a0]+/g, ' ').trim();
}

function escapeTitolo(titolo: string): string {
  return String(titolo || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Chiave stabile di una sezione: minuscole, senza accenti, numerazione iniziale
 * tolta, non alfanumerici ⇒ `_`. `'3. Vicenda Sermetra'` ⇒ `'vicenda_sermetra'`;
 * vuoto ⇒ `'sezione'`.
 */
const MAX_CHIAVE = 80;

export function chiaveSezione(titolo: string): string {
  const base = String(titolo || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const pulisci = (s: string) => s.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const senzaNumero = base.replace(/^\s*\d+(?:\.\d+)*\s*[.):\-\u2013\u2014]?\s*/, '');
  const chiave = pulisci(senzaNumero) || pulisci(base) || 'sezione';
  // Al più 80 caratteri, come il backend (il PUT delle spunte accetta chiavi fino a 100)
  return chiave.length > MAX_CHIAVE ? chiave.slice(0, MAX_CHIAVE).replace(/_+$/, '') : chiave;
}

/**
 * Sezioni con la posizione del loro corpo nell'HTML originale.
 * Senza `<h2>` ⇒ una sola sezione `preambolo` (anche vuota); con `<h2>` il
 * preambolo c'è solo se contiene qualcosa oltre agli spazi.
 */
function analizza(html: string): Parte[] {
  const sorgente = typeof html === 'string' ? html : '';
  const titoli: { inizio: number; fine: number; interno: string }[] = [];
  const re = new RegExp(RE_H2.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(sorgente)) !== null) {
    titoli.push({ inizio: m.index, fine: m.index + m[0].length, interno: m[1] });
  }
  if (titoli.length === 0) {
    return [{ chiave: CHIAVE_PREAMBOLO, titolo: '', inizioCorpo: 0, fineCorpo: sorgente.length }];
  }
  const parti: Parte[] = [];
  if (sorgente.slice(0, titoli[0].inizio).trim() !== '') {
    parti.push({ chiave: CHIAVE_PREAMBOLO, titolo: '', inizioCorpo: 0, fineCorpo: titoli[0].inizio });
  }
  // 'preambolo' è sempre riservata: un <h2>Preambolo</h2> diventa 'preambolo_2'.
  const usate: Record<string, boolean> = Object.create(null);
  usate[CHIAVE_PREAMBOLO] = true;
  titoli.forEach((t, i) => {
    const titolo = testoTitolo(t.interno);
    const base = chiaveSezione(titolo);
    let chiave = base;
    let n = 2;
    while (usate[chiave]) {
      chiave = `${base}_${n}`;
      n += 1;
    }
    usate[chiave] = true;
    parti.push({
      chiave,
      titolo,
      inizioCorpo: t.fine,
      fineCorpo: i + 1 < titoli.length ? titoli[i + 1].inizio : sorgente.length,
    });
  });
  return parti;
}

/** `[{ chiave, titolo, html }]`: `html` è il corpo senza l'`<h2>`. */
export function dividiInSezioni(html: string): Sezione[] {
  const sorgente = typeof html === 'string' ? html : '';
  return analizza(sorgente).map((p) => ({
    chiave: p.chiave,
    titolo: p.titolo,
    html: sorgente.slice(p.inizioCorpo, p.fineCorpo),
  }));
}

/** Inverso di `dividiInSezioni`: il preambolo senza titolo, le altre con `<h2>`. */
export function componiSezioni(sezioni: Sezione[]): string {
  return (Array.isArray(sezioni) ? sezioni : [])
    .map((s) => (s.chiave === CHIAVE_PREAMBOLO ? s.html || '' : `<h2>${escapeTitolo(s.titolo)}</h2>${s.html || ''}`))
    .join('');
}

/**
 * Sostituisce il corpo della sezione `chiave` lasciando il resto dell'HTML
 * byte per byte com'era (attributi degli `<h2>` compresi). Chiave assente ⇒
 * html invariato.
 */
export function sostituisciSezione(html: string, chiave: string, nuovoCorpo: string): string {
  if (typeof html !== 'string') return html;
  const parte = analizza(html).find((p) => p.chiave === chiave);
  if (!parte) return html;
  return html.slice(0, parte.inizioCorpo) + String(nuovoCorpo || '') + html.slice(parte.fineCorpo);
}

export const STRUTTURA_PREDEFINITA: ReadonlyArray<Readonly<{ titolo: string }>> = Object.freeze([
  Object.freeze({ titolo: 'Intestazione' }),
  Object.freeze({ titolo: 'Presenti' }),
  Object.freeze({ titolo: 'Ordine del giorno' }),
  Object.freeze({ titolo: 'Svolgimento della riunione' }),
  Object.freeze({ titolo: 'Deliberazioni e impegni' }),
  Object.freeze({ titolo: 'Chiusura' }),
]);

/**
 * HTML del verbale vuoto: un `<h2>` per sezione della struttura, corpo `<p></p>`. Con `puntiOdg`
 * (ordine del giorno dell'evento) la sezione «Ordine del giorno» ha già l'elenco numerato; con
 * `senzaOdg` (l'evento non ha un ordine del giorno) la sezione non c'è.
 */
export function scheletroVerbale({ puntiOdg = null, senzaOdg = false }: { puntiOdg?: string[] | null; senzaOdg?: boolean } = {}): string {
  const punti = Array.isArray(puntiOdg) ? puntiOdg.filter((p) => typeof p === 'string' && p.trim()) : [];
  return componiSezioni(
    STRUTTURA_PREDEFINITA
      .map((s) => ({ chiave: chiaveSezione(s.titolo), titolo: s.titolo, html: '<p></p>' }))
      .filter((s) => !(senzaOdg && s.chiave === 'ordine_del_giorno'))
      .map((s) => (s.chiave === 'ordine_del_giorno' && punti.length
        ? { ...s, html: `<ol>${punti.map((p) => `<li>${escapeTitolo(p.trim())}</li>`).join('')}</ol>` }
        : s))
  );
}

/* ---------- Solo sito: partecipanti esterni nella sezione «Presenti» ---------- */

export type PartecipanteEsternoTesto = { nome: string; ruolo?: string };

const RE_INIZIO_ESTERNI = /^partecipanti\s+esterni/i;

/** Blocco (paragrafo o titolo h3/h4) dentro un corpo di sezione. */
const RE_BLOCCO_ESTERNI = /<(p|h3|h4)(?:\s[^>]*)?>([\s\S]*?)<\/\1\s*>/gi;

function iniziaConEsterni(htmlInterno: string): boolean {
  return RE_INIZIO_ESTERNI.test(testoTitolo(htmlInterno));
}

/** `<p><strong>Partecipanti esterni e ospiti:</strong> Nome (ruolo), …</p>` (o «nessuno»). */
function paragrafoEsterni(esterni: PartecipanteEsternoTesto[]): string {
  const voci = (Array.isArray(esterni) ? esterni : [])
    .map((p) => ({ nome: String(p?.nome || '').trim(), ruolo: String(p?.ruolo || '').trim() }))
    .filter((p) => p.nome)
    .map((p) => (p.ruolo ? `${escapeTitolo(p.nome)} (${escapeTitolo(p.ruolo)})` : escapeTitolo(p.nome)));
  return `<p><strong>Partecipanti esterni e ospiti:</strong> ${voci.length > 0 ? voci.join(', ') : 'nessuno'}</p>`;
}

/**
 * Riporta i partecipanti esterni nella sezione «Presenti» (chiave `presenti`, altrimenti la
 * prima sezione con «present» nel titolo): il paragrafo (o il titolo h3/h4) che inizia con
 * «Partecipanti esterni» è sostituito; un `<h2>` «Partecipanti esterni…» subito dopo i
 * Presenti (titolo nato per errore) diventa il paragrafo; se manca ed esterni c'è, il
 * paragrafo si aggiunge in fondo ai Presenti. Senza sezione Presenti ⇒ html invariato.
 */
export function aggiornaPartecipantiEsterni(html: string, esterni: PartecipanteEsternoTesto[]): string {
  if (typeof html !== 'string') return html;
  const parti = analizza(html).filter((p) => p.chiave !== CHIAVE_PREAMBOLO);
  const presenti = parti.find((p) => p.chiave === 'presenti')
    || parti.find((p) => /present/i.test(p.titolo));
  if (!presenti) return html;

  const paragrafo = paragrafoEsterni(esterni);
  const corpo = html.slice(presenti.inizioCorpo, presenti.fineCorpo);

  const re = new RegExp(RE_BLOCCO_ESTERNI.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(corpo)) !== null) {
    if (iniziaConEsterni(m[2])) {
      const inizio = presenti.inizioCorpo + m.index;
      return html.slice(0, inizio) + paragrafo + html.slice(inizio + m[0].length);
    }
  }

  // Titolo h2 «Partecipanti esterni…» subito dopo i Presenti: il suo <h2> diventa il paragrafo.
  const indice = parti.indexOf(presenti);
  const seguente = parti[indice + 1];
  if (seguente && RE_INIZIO_ESTERNI.test(seguente.titolo)) {
    const re2 = new RegExp(RE_H2.source, 'gi');
    re2.lastIndex = presenti.fineCorpo;
    const h2 = re2.exec(html);
    if (h2 && h2.index === presenti.fineCorpo) {
      return html.slice(0, h2.index) + paragrafo + html.slice(h2.index + h2[0].length);
    }
  }

  const haVoci = (Array.isArray(esterni) ? esterni : []).some((p) => String(p?.nome || '').trim());
  if (!haVoci) return html;
  return html.slice(0, presenti.fineCorpo) + paragrafo + html.slice(presenti.fineCorpo);
}

/* ---------- Solo sito: controllo prima della pubblicazione ---------- */

/** Un corpo «ha contenuto» se ha testo visibile o un elemento multimediale. */
function haContenuto(html: string): boolean {
  if (/<(img|iframe|video|audio|object|embed)\b/i.test(html || '')) return true;
  return testoDaHtml(html).replace(/[\s\u00a0\u200b\ufeff]+/g, '') !== '';
}

const RE_DA_VERIFICARE = /\[\s*da\s+verificare\s*\]/gi;

/** Stessa forma di RE_DA_VERIFICARE, per cercare i segni nel testo del foglio (evidenziazione). */
export const FORMA_DA_VERIFICARE = RE_DA_VERIFICARE.source;

/** Quanti `[da verificare]` ci sono nel testo visibile dell'HTML. */
export function contaDaVerificare(html: string): number {
  return (testoDaHtml(typeof html === 'string' ? html : '').match(RE_DA_VERIFICARE) || []).length;
}

/**
 * Controllo finale prima di «Pubblica»: `blocco` impedisce la conferma (verbale
 * senza testo né allegati, come il backend); gli `avviso` si possono ignorare.
 * Le chiavi di `sezioniRiviste` che non esistono più nel testo sono ignorate.
 * `vociDaAssociare` (piano 2): voci della trascrizione senza persona ⇒ avviso, mai blocco.
 * `foto`/`socialRichiesto` (piano 3): foto caricate senza la proposta del post ⇒ avviso, mai blocco.
 */
export function controlloFinale({
  contenuto,
  sezioniRiviste,
  allegati,
  vociDaAssociare = 0,
  foto = 0,
  socialRichiesto = false,
  partecipantiEsterni = [],
}: {
  contenuto: string;
  sezioniRiviste: string[];
  allegati: unknown[];
  vociDaAssociare?: number;
  foto?: number;
  socialRichiesto?: boolean;
  /** Partecipanti esterni dei dettagli: chi non compare nel testo ⇒ avviso, mai blocco. */
  partecipantiEsterni?: PartecipanteEsternoTesto[];
}): EsitoControllo[] {
  const numeroFoto = Number.isFinite(foto) ? Math.max(0, Math.floor(foto)) : 0;
  const avvisoSocial: EsitoControllo[] = numeroFoto > 0 && !socialRichiesto
    ? [{ livello: 'avviso', testo: `Hai caricato ${numeroFoto} foto ma non hai proposto un post social` }]
    : [];

  const sezioni = dividiInSezioni(typeof contenuto === 'string' ? contenuto : '');
  const numeroAllegati = Array.isArray(allegati) ? allegati.length : 0;
  const haTesto = sezioni.some((s) => haContenuto(s.html));

  if (!haTesto) {
    if (numeroAllegati === 0) {
      return [
        {
          livello: 'blocco',
          testo: 'Il verbale è vuoto: scrivi il testo o aggiungi almeno un allegato prima di pubblicarlo.',
        },
      ];
    }
    return [{ livello: 'avviso', testo: 'Il verbale non ha testo: gli invitati vedranno solo gli allegati.' }, ...avvisoSocial];
  }

  const esiti: EsitoControllo[] = [];

  sezioni.forEach((s) => {
    if (s.chiave !== CHIAVE_PREAMBOLO && !haContenuto(s.html)) {
      esiti.push({ livello: 'avviso', testo: `La sezione «${s.titolo || 'senza titolo'}» è vuota` });
    }
  });

  const daVerificare = contaDaVerificare(contenuto);
  if (daVerificare > 0) {
    const dove = sezioni
      .filter((s) => contaDaVerificare(s.html) > 0)
      .map((s) => (s.chiave === CHIAVE_PREAMBOLO ? 'in apertura' : `«${s.titolo || 'senza titolo'}»`));
    const inSezioni = dove.length ? ` (${dove.join(', ')})` : '';
    esiti.push({
      livello: 'avviso',
      testo: daVerificare === 1
        ? `È rimasto 1 punto segnato [da verificare]${inSezioni}: è evidenziato in giallo nel foglio. Completa il dato mancante o togli il segno.`
        : `Sono rimasti ${daVerificare} punti segnati [da verificare]${inSezioni}: sono evidenziati in giallo nel foglio. Completa i dati mancanti o togli il segno.`,
    });
  }

  const riviste: Record<string, boolean> = Object.create(null);
  (Array.isArray(sezioniRiviste) ? sezioniRiviste : []).forEach((chiave) => {
    riviste[String(chiave)] = true;
  });
  const nonRiviste = sezioni.filter(
    (s) => !(s.chiave === CHIAVE_PREAMBOLO && !haContenuto(s.html)) && !riviste[s.chiave]
  ).length;
  if (nonRiviste === 1) {
    esiti.push({ livello: 'avviso', testo: '1 sezione non segnata come rivista' });
  } else if (nonRiviste > 1) {
    esiti.push({ livello: 'avviso', testo: `${nonRiviste} sezioni non segnate come riviste` });
  }

  const voci = Number.isFinite(vociDaAssociare) ? Math.max(0, Math.floor(vociDaAssociare)) : 0;
  if (voci === 1) {
    esiti.push({ livello: 'avviso', testo: '1 voce non associata a una persona' });
  } else if (voci > 1) {
    esiti.push({ livello: 'avviso', testo: `${voci} voci non associate a una persona` });
  }

  const compatta = (s: string) => s.replace(/[\s ]+/g, ' ').trim().toLowerCase();
  const testoCompatto = compatta(testoDaHtml(contenuto));
  (Array.isArray(partecipantiEsterni) ? partecipantiEsterni : []).forEach((p) => {
    const nome = String(p?.nome || '').replace(/[\s ]+/g, ' ').trim();
    if (nome && !testoCompatto.includes(compatta(nome))) {
      esiti.push({ livello: 'avviso', testo: `Il partecipante esterno «${nome}» non compare nel testo` });
    }
  });

  esiti.push(...avvisoSocial);
  return esiti;
}
